import type { ICatalystHandInContext, IContextualChat } from '@1js/catalyst';
import type { ContentItemType } from '@1js/scc-types';
import { create } from 'zustand';

import type { GlobalCopilotContextData } from './types';

const catalystAtdVariant = '-agt_module_atd_v2';

export type CatalystLifecycle = 'failed' | 'generating' | 'idle' | 'initial' | 'ready' | 'warming';

interface CatalystSDKState {
  readonly lifecycle: CatalystLifecycle;
  readonly nextHandIn: ICatalystHandInContext | null;
  readonly rendererMountVersion: number;
}

const useCatalystStore = create<CatalystSDKState>(() => ({
  lifecycle: 'initial',
  nextHandIn: null,
  rendererMountVersion: 0,
}));

export const getCatalystLifecycle = (): CatalystLifecycle => useCatalystStore.getState().lifecycle;

export const useCatalystLifecycle = (): CatalystLifecycle => useCatalystStore((state) => state.lifecycle);

export const getCatalystRendererMountVersion = (): number => useCatalystStore.getState().rendererMountVersion;

export const useCatalystRendererMountVersion = (): number => useCatalystStore((state) => state.rendererMountVersion);

export const requestCatalystPrewarm = (): void => {
  if (getCatalystLifecycle() === 'initial') {
    useCatalystStore.setState({ lifecycle: 'warming' });
  }
};

export const markCatalystRendererStarted = (): void => {
  useCatalystStore.setState({ lifecycle: 'warming' });
};

export const markCatalystReady = (): void => {
  if (getCatalystLifecycle() === 'warming') {
    useCatalystStore.setState({ lifecycle: 'ready' });
  }
};

export const markCatalystChatReady = (): void => {
  const lifecycle = getCatalystLifecycle();

  if (lifecycle !== 'failed' && lifecycle !== 'generating') {
    useCatalystStore.setState({ lifecycle: 'idle' });
  }
};

export const markCatalystGenerating = (): void => {
  if (getCatalystLifecycle() !== 'failed') {
    useCatalystStore.setState({ lifecycle: 'generating' });
  }
};

export const markCatalystIdle = (): void => {
  if (getCatalystLifecycle() !== 'failed') {
    useCatalystStore.setState({ lifecycle: 'idle' });
  }
};

export const markCatalystFailed = (): void => {
  useCatalystStore.setState({ lifecycle: 'failed' });
};

export const retryCatalystRenderer = (): void => {
  if (getCatalystLifecycle() === 'failed') {
    useCatalystStore.setState((state) => ({
      lifecycle: 'warming',
      rendererMountVersion: state.rendererMountVersion + 1,
    }));
  }
};

export const resetCatalystLifecycle = (): void => {
  useCatalystStore.setState({ lifecycle: 'initial', rendererMountVersion: 0 });
};

export const buildVivaEngageContentItems = (
  context: GlobalCopilotContextData
): ICatalystHandInContext['contentItems'] => {
  const contentItem = { contentType: 'vivaEngage' as ContentItemType.VIVAENGAGE, ...context };
  delete contentItem.supportedScenario;

  return [contentItem];
};

export const buildVivaEngageDataContext = (context: GlobalCopilotContextData): IContextualChat => {
  const contentItems = buildVivaEngageContentItems(context);
  const dataContext = {
    contentType: 'vivaEngage' as ContentItemType.VIVAENGAGE,
    contentItems,
    contextAttributes: [{ key: 'SupportedScenario', value: context.supportedScenario ?? 'ViewHome' }],
    serverVariants: [catalystAtdVariant],
  };

  return dataContext;
};

export const getNextHandIn = (): ICatalystHandInContext | null => useCatalystStore.getState().nextHandIn;

export const useNextHandIn = (): ICatalystHandInContext | null => useCatalystStore((state) => state.nextHandIn);

export const setNextHandIn = (promptText: string, context: GlobalCopilotContextData): void => {
  const nextHandIn = {
    ...buildVivaEngageDataContext(context),
    promptText,
    isNewChat: true,
  };

  useCatalystStore.setState({
    nextHandIn,
  });
};

export const clearNextHandIn = (): void => {
  useCatalystStore.setState({ nextHandIn: null });
};
